import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Badge, Button, Page, PageHead } from '../components/ui';
import { DIET_PLANS, MACROS, WORKOUT_PLANS, n0 } from '../lib/format';

function Selectable({ active, onSelect, title, sub, children }) {
  return (
    <section
      className="card"
      style={active ? { borderColor: 'var(--accent)', boxShadow: '0 0 0 1px var(--accent-line)' } : undefined}
    >
      <div className="card-head">
        <div className="grow">
          <h2>{title}</h2>
          <div className="sub">{sub}</div>
        </div>
        {active
          ? <Badge tone="accent"><Check size={12} /> Active</Badge>
          : <Button size="sm" onClick={onSelect}>Select</Button>}
      </div>
      <div className="card-body">{children}</div>
    </section>
  );
}

export default function Plans({ plan, onSelectDiet, onSelectWorkout }) {
  const [saving, setSaving] = useState(false);

  const choose = async (fn, value) => {
    if (saving) return;
    setSaving(true);
    try { await fn(value); } finally { setSaving(false); }
  };

  return (
    <Page>
      <PageHead title="Plans">
        Pick a diet and a training split. Your choice sets the macro targets on the dashboard and
        steers the videos you are shown.
      </PageHead>

      <div>
        <h2 style={{ marginBottom: 12 }}>Diet</h2>
        <div className="grid grid-3">
          {Object.entries(DIET_PLANS).map(([key, d]) => (
            <Selectable
              key={key}
              active={plan?.diet_plan === key}
              onSelect={() => choose(onSelectDiet, key)}
              title={d.name}
              sub={d.description}
            >
              <div className="grid grid-4" style={{ gap: 10, marginBottom: 14 }}>
                {MACROS.map((m) => (
                  <div key={m.key}>
                    <div className="label">{m.label}</div>
                    <div className="num" style={{ fontSize: 15, fontWeight: 700, color: m.color, marginTop: 2 }}>
                      {n0(d[m.key])}<span className="faint" style={{ fontSize: 10, fontWeight: 500 }}>{m.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="stack-tight" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                {d.meals.map((m) => (
                  <div key={m.meal}>
                    <div className="label">{m.meal}</div>
                    <div className="dim" style={{ fontSize: 12.5 }}>{m.items}</div>
                  </div>
                ))}
              </div>
            </Selectable>
          ))}
        </div>
      </div>

      <div>
        <h2 style={{ marginBottom: 12 }}>Training</h2>
        <div className="grid grid-3">
          {Object.entries(WORKOUT_PLANS).map(([key, w]) => (
            <Selectable
              key={key}
              active={plan?.workout_plan === key}
              onSelect={() => choose(onSelectWorkout, key)}
              title={w.name}
              sub={w.description}
            >
              <div className="stack-tight">
                {w.schedule.map((day) => (
                  <div key={day.day}>
                    <div className="spread" style={{ marginBottom: 3 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{day.day}</span>
                      <span className="faint" style={{ fontSize: 11 }}>{day.exercises.length} lifts</span>
                    </div>
                    <div className="dim" style={{ fontSize: 12 }}>
                      {day.exercises.map((e) => `${e.name} ${e.sets}×${e.reps}`).join(' · ')}
                    </div>
                  </div>
                ))}
              </div>
            </Selectable>
          ))}
        </div>
      </div>
    </Page>
  );
}
